import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AnimatePresence,
  motion,
} from "framer-motion";

import {
  useTranslation,
} from "react-i18next";

import {
  useSearchParams,
} from "react-router-dom";
import portfolioData from "../../../site/data/portfolioData";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  PORTFOLIO WALL
  ============================================================

  Puhdas kuvagalleria.

  - ei albumikirjoja
  - ei portfoliojuttujen tekstikortteja
  - ei aukeamia
  - ei numeroituja albumeita
  - luonnolliset kuvasuhteet
  - tiivis masonry-seinä
  - kategoriat
  - pehmeät animaatiot
  - lataa lisää
  - fullscreen lightbox
*/

const INITIAL_VISIBLE = 12;
const LOAD_STEP = 8;

/*
  ============================================================
  LOCALIZED VALUE
  ============================================================
*/

function getLocalizedValue(
  value,
  language
) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  if (
    typeof value ===
    "string"
  ) {
    return value;
  }

  return (
    value[language] ??
    value.fi ??
    value.en ??
    ""
  );
}

/*
  ============================================================
  COMPONENT
  ============================================================
*/

export default function AlbumShowcase({
  language = "fi",
}) {
  const {
    t,
  } = useTranslation(
    "common"
  );

  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();

  const requestedCategory =
    searchParams.get(
      "category"
    );
  const [
    selectedCategory,
    setSelectedCategory,
  ] = useState(
    "all"
  );

  const [
    visibleCount,
    setVisibleCount,
  ] = useState(
    INITIAL_VISIBLE
  );

  const [
    lightboxIndex,
    setLightboxIndex,
  ] = useState(
    null
  );

  /*
    ============================================================
    ENABLED / ORDERED ITEMS
    ============================================================
  */

  const portfolioItems =
    useMemo(
      () =>
        (
          portfolioData.items ||
          []
        )
          .filter(
            (item) =>
              item.enabled !==
              false
          )
          .sort(
            (a, b) =>
              (a.order || 0) -
              (b.order || 0)
          ),
      []
    );

  /*
    ============================================================
    CATEGORIES
    ============================================================
  */

  const categories =
    useMemo(
      () => [
        {
          slug: "all",
          name:
            portfolioData
              .labels
              ?.all,
        },

        ...(
          portfolioData.categories ||
          []
        )
          .filter(
            (category) =>
              category.enabled !==
              false
          )
          .sort(
            (a, b) =>
              (a.order || 0) -
              (b.order || 0)
          ),
      ],
      []
    );

  const validCategorySlugs =
    useMemo(
      () =>
        categories.map(
          (category) =>
            category.slug
        ),
      [
        categories,
      ]
    );

  /*
    ============================================================
    URL CATEGORY SYNC
    ============================================================

    Esimerkiksi:

    /portfolio?category=weddings
    /en/portfolio?category=weddings

    avaa suoraan Häät / Weddings -kategorian.
  */

  useEffect(() => {
    let nextCategory =
      "all";

    if (
      requestedCategory &&
      validCategorySlugs.includes(
        requestedCategory
      )
    ) {
      nextCategory =
        requestedCategory;
    }

    setSelectedCategory(
      (current) =>
        current ===
        nextCategory
          ? current
          : nextCategory
    );

    setVisibleCount(
      INITIAL_VISIBLE
    );

    setLightboxIndex(
      null
    );
  }, [
    requestedCategory,
    validCategorySlugs,
  ]);
  /*
    ============================================================
    ALL IMAGES
    ============================================================

    "Kaikki"-näkymässä kuvat sekoitetaan hallitusti
    kategorioiden kesken.

    Ensin:
    - wedding-01
    - couple-01
    - portrait-01
    - editorial-01
    - jne.

    Sitten kaikkien kakkoskuvat jne.

    Näin ensimmäiset 12 kuvaa eivät ole vain muutamasta
    ensimmäisestä kategoriasta.
  */

  const allImages =
    useMemo(
      () => {
        const maxImageCount =
          Math.max(
            0,
            ...portfolioItems.map(
              (item) =>
                (
                  item.images ||
                  []
                ).length
            )
          );

        const result = [];

        for (
          let imageIndex = 0;
          imageIndex <
          maxImageCount;
          imageIndex += 1
        ) {
          portfolioItems.forEach(
            (item) => {
              const image =
                (
                  item.images ||
                  []
                )[imageIndex];

              if (!image) {
                return;
              }

              result.push({
                id:
                  `${item.id}-${imageIndex}`,

                url:
                  image.url,

                alt:
                  image.alt,

                categories:
                  item.categories ||
                  [],

                imageIndex,
              });
            }
          );
        }

        return result;
      },
      [
        portfolioItems,
      ]
    );

  /*
    ============================================================
    FILTERED IMAGES
    ============================================================
  */

  const filteredImages =
    useMemo(
      () => {
        if (
          selectedCategory ===
          "all"
        ) {
          return allImages;
        }

        return allImages.filter(
          (image) =>
            image.categories.includes(
              selectedCategory
            )
        );
      },
      [
        allImages,
        selectedCategory,
      ]
    );

  const visibleImages =
    filteredImages.slice(
      0,
      visibleCount
    );

  const hasMore =
    visibleCount <
    filteredImages.length;

  /*
    ============================================================
    CATEGORY CHANGE
    ============================================================
  */

  function handleCategoryChange(
    categorySlug
  ) {
    setSelectedCategory(
      categorySlug
    );

    setVisibleCount(
      INITIAL_VISIBLE
    );

    setLightboxIndex(
      null
    );

    const nextSearchParams =
      new URLSearchParams(
        searchParams
      );

    if (
      categorySlug ===
      "all"
    ) {
      nextSearchParams.delete(
        "category"
      );
    }

    if (
      categorySlug !==
      "all"
    ) {
      nextSearchParams.set(
        "category",
        categorySlug
      );
    }

    setSearchParams(
      nextSearchParams,
      {
        replace: true,
      }
    );
  }

  /*
    ============================================================
    SCROLL TO PORTFOLIO FROM SESSION LINK
    ============================================================
  */

  useEffect(() => {
    if (
      window.location.hash !==
      "#portfolio-gallery"
    ) {
      return undefined;
    }

    const frame =
      window.requestAnimationFrame(
        () => {
          const gallery =
            document.getElementById(
              "portfolio-gallery"
            );

          if (!gallery) {
            return;
          }

          const top =
            gallery.getBoundingClientRect()
              .top +
            window.scrollY -
            90;

          window.scrollTo({
            top,
            behavior: "smooth",
          });
        }
      );

    return () => {
      window.cancelAnimationFrame(
        frame
      );
    };
  }, [
    requestedCategory,
  ]);
  /*
    ============================================================
    LIGHTBOX
    ============================================================
  */

  useEffect(() => {
    if (
      lightboxIndex ===
      null
    ) {
      return undefined;
    }

    const previousOverflow =
      document.body.style
        .overflow;

    document.body.style
      .overflow =
      "hidden";

    function handleKeyDown(
      event
    ) {
      if (
        event.key ===
        "Escape"
      ) {
        setLightboxIndex(
          null
        );
      }

      if (
        event.key ===
          "ArrowLeft" &&
        visibleImages.length >
          1
      ) {
        setLightboxIndex(
          (current) =>
            (
              current -
                1 +
              visibleImages.length
            ) %
            visibleImages.length
        );
      }

      if (
        event.key ===
          "ArrowRight" &&
        visibleImages.length >
          1
      ) {
        setLightboxIndex(
          (current) =>
            (
              current +
              1
            ) %
            visibleImages.length
        );
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.style
        .overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [
    lightboxIndex,
    visibleImages.length,
  ]);

  const activeLightboxImage =
    lightboxIndex !==
      null
      ? visibleImages[
          lightboxIndex
        ]
      : null;

  /*
    ============================================================
    RENDER
    ============================================================
  */

  return (
    <section
      id="portfolio-gallery"
      className="
        bg-canvas
        pb-20
        pt-8

        sm:pb-28
        sm:pt-10

        lg:pb-32
      "
      aria-label={
        language === "en"
          ? "Portfolio gallery"
          : "Portfoliogalleria"
      }
    >
      {/* ======================================================
          FILTERS
          ====================================================== */}

      <div
        className="
          mx-auto
          mb-7
          w-full
          max-w-[1800px]
          px-4

          sm:mb-9
          sm:px-6
        "
      >
        <div
          className="
            flex
            flex-wrap
            items-center
            gap-x-5
            gap-y-3
            border-b
            border-border
            pb-5

            sm:gap-x-7
          "
        >
          {categories.map(
            (category) => {
              const isActive =
                selectedCategory ===
                category.slug;

              return (
                <button
                  key={
                    category.slug
                  }
                  type="button"
                  onClick={() =>
                    handleCategoryChange(
                      category.slug
                    )
                  }
                  aria-pressed={
                    isActive
                  }
                  className={`
                    relative
                    border-0
                    bg-transparent
                    pb-1
                    text-[9px]
                    font-medium
                    uppercase
                    tracking-[0.18em]
                    transition-colors
                    duration-300

                    sm:text-[10px]

                    ${
                      isActive
                        ? "text-text"
                        : "text-muted hover:text-text"
                    }
                  `}
                >
                  {getLocalizedValue(
                    category.name,
                    language
                  )}

                  <span
                    className={`
                      absolute
                      bottom-[-1px]
                      left-0
                      h-px
                      bg-text
                      transition-all
                      duration-500

                      ${
                        isActive
                          ? "w-full"
                          : "w-0"
                      }
                    `}
                    aria-hidden="true"
                  />
                </button>
              );
            }
          )}
        </div>
      </div>

      {/* ======================================================
          MASONRY / WALL
          ====================================================== */}

      <div
        className="
          mx-auto
          w-full
          max-w-[1800px]
          columns-2
          gap-[4px]
          px-[4px]

          sm:gap-[6px]
          sm:px-[6px]

          md:columns-3

          lg:columns-4
        "
      >
        <AnimatePresence
          mode="popLayout"
        >
          {visibleImages.map(
            (
              image,
              index
            ) => (
              <motion.figure
                key={
                  image.id
                }
                layout
                className="
                  mb-[4px]
                  break-inside-avoid

                  sm:mb-[6px]
                "
                initial={{
                  opacity: 0,
                  y: 18,
                  scale: 0.992,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                }}
                viewport={{
                  once: true,
                  margin:
                    "0px 0px -5% 0px",
                }}
                transition={{
                  duration: 0.7,

                  delay:
                    Math.min(
                      index *
                        0.025,
                      0.18
                    ),

                  ease: [
                    0.22,
                    1,
                    0.36,
                    1,
                  ],
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    setLightboxIndex(
                      index
                    )
                  }
                  className="
                    group
                    relative
                    block
                    w-full
                    cursor-zoom-in
                    overflow-hidden
                    border-0
                    bg-transparent
                    p-0
                  "
                  aria-label={
                    getLocalizedValue(
                      image.alt,
                      language
                    ) ||
                    t(
                      "portfolioPage.openImage"
                    )
                  }
                >
                  <img
                    src={
                      image.url
                    }
                    alt={getLocalizedValue(
                      image.alt,
                      language
                    )}
                    loading={
                      index < 8
                        ? "eager"
                        : "lazy"
                    }
                    decoding="async"
                    draggable="false"
                    className="
                      block
                      h-auto
                      w-full
                      transition
                      duration-[1000ms]
                      ease-out

                      group-hover:scale-[1.018]
                      group-hover:brightness-[0.96]
                    "
                  />

                  <span
                    className="
                      pointer-events-none
                      absolute
                      inset-0
                      border
                      border-white/0
                      transition-colors
                      duration-500

                      group-hover:border-white/30
                    "
                    aria-hidden="true"
                  />
                </button>
              </motion.figure>
            )
          )}
        </AnimatePresence>
      </div>

      {/* ======================================================
          LOAD MORE
          ====================================================== */}

      {hasMore && (
        <motion.div
          className="
            flex
            justify-center
            px-6
            pt-14

            sm:pt-16
          "
          initial={{
            opacity: 0,
            y: 12,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.6,
          }}
        >
          <button
            type="button"
            onClick={() =>
              setVisibleCount(
                (current) =>
                  current +
                  LOAD_STEP
              )
            }
            className="
              group
              inline-flex
              min-h-12
              items-center
              gap-5
              border
              border-border
              bg-transparent
              px-8
              py-3
              text-[9px]
              font-semibold
              uppercase
              tracking-[0.2em]
              text-text
              transition
              duration-300

              hover:border-text

              sm:px-10
              sm:text-[10px]
            "
          >
            {t(
              "portfolioPage.loadMore"
            )}

            <span
              className="
                text-base
                font-light
                transition-transform
                duration-300
                group-hover:translate-y-0.5
              "
              aria-hidden="true"
            >
              ↓
            </span>
          </button>
        </motion.div>
      )}

      {/* ======================================================
          LIGHTBOX
          ====================================================== */}

      <AnimatePresence>
        {activeLightboxImage && (
          <motion.div
            className="
              fixed
              inset-0
              z-[150]
              flex
              items-center
              justify-center
              bg-black/95
              p-4
              text-white
              backdrop-blur-sm

              sm:p-8
            "
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            transition={{
              duration: 0.3,
            }}
            onMouseDown={(
              event
            ) => {
              if (
                event.target ===
                event.currentTarget
              ) {
                setLightboxIndex(
                  null
                );
              }
            }}
            role="dialog"
            aria-modal="true"
            aria-label={t(
              "portfolioPage.lightbox"
            )}
          >
            {/* CLOSE */}

            <button
              type="button"
              onClick={() =>
                setLightboxIndex(
                  null
                )
              }
              className="
                absolute
                right-4
                top-4
                z-20
                flex
                h-11
                w-11
                items-center
                justify-center
                rounded-full
                border
                border-white/30
                bg-black/20
                text-xl
                font-light
                text-white
                backdrop-blur-md
                transition
                hover:border-white

                sm:right-7
                sm:top-7
              "
              aria-label={t(
                "portfolioPage.closeImage"
              )}
            >
              ×
            </button>

            {/* IMAGE */}

            <motion.img
              key={
                activeLightboxImage.url
              }
              src={
                activeLightboxImage.url
              }
              alt={getLocalizedValue(
                activeLightboxImage.alt,
                language
              )}
              draggable="false"
              className="
                max-h-[88dvh]
                max-w-[94vw]
                object-contain
              "
              initial={{
                opacity: 0,
                scale: 0.985,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                scale: 0.99,
              }}
              transition={{
                duration: 0.4,
                ease: [
                  0.22,
                  1,
                  0.36,
                  1,
                ],
              }}
            />

            {/* PREVIOUS */}

            {visibleImages.length >
              1 && (
              <button
                type="button"
                onClick={() =>
                  setLightboxIndex(
                    (current) =>
                      (
                        current -
                          1 +
                        visibleImages.length
                      ) %
                      visibleImages.length
                  )
                }
                className="
                  absolute
                  bottom-5
                  left-5
                  flex
                  h-11
                  w-11
                  items-center
                  justify-center
                  rounded-full
                  border
                  border-white/25
                  bg-black/20
                  text-lg
                  text-white
                  backdrop-blur-md
                  transition
                  hover:border-white

                  sm:bottom-auto
                  sm:left-7
                  sm:top-1/2
                  sm:-translate-y-1/2
                "
                aria-label={t(
                  "portfolioPage.previous"
                )}
              >
                ←
              </button>
            )}

            {/* NEXT */}

            {visibleImages.length >
              1 && (
              <button
                type="button"
                onClick={() =>
                  setLightboxIndex(
                    (current) =>
                      (
                        current +
                          1
                      ) %
                      visibleImages.length
                  )
                }
                className="
                  absolute
                  bottom-5
                  right-5
                  flex
                  h-11
                  w-11
                  items-center
                  justify-center
                  rounded-full
                  border
                  border-white/25
                  bg-black/20
                  text-lg
                  text-white
                  backdrop-blur-md
                  transition
                  hover:border-white

                  sm:bottom-auto
                  sm:right-7
                  sm:top-1/2
                  sm:-translate-y-1/2
                "
                aria-label={t(
                  "portfolioPage.next"
                )}
              >
                →
              </button>
            )}

            {/* COUNTER */}

            <div
              className="
                pointer-events-none
                absolute
                bottom-6
                left-1/2
                -translate-x-1/2
                text-[9px]
                uppercase
                tracking-[0.2em]
                text-white/60
              "
            >
              {String(
                lightboxIndex +
                  1
              ).padStart(
                2,
                "0"
              )}

              {" / "}

              {String(
                visibleImages.length
              ).padStart(
                2,
                "0"
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}